import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import EscortForm from "./EscortForm";
import { createEscortForm } from "../../store/slices/escortFormSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";

const AddEscortForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    case_id: caseId || "",
    patient_name: "",
    cnic_bform: "",
    age_gender: "",
    residential_address: "",
    date_of_admission_request: new Date().toISOString().split("T")[0],

    applicant_name: "",
    applicant_cnic: "",
    applicant_relationship: "",
    applicant_contact: "",
    applicant_address: "",

    ground_mental_disorder: false,
    ground_harm_self: false,
    ground_harm_others: false,
    ground_unable_care_self: false,
    ground_refuses_voluntary: false,

    signature_applicant_name: "",
    signature_applicant_date: new Date().toISOString().split("T")[0],

    psychiatrist_name: "",
    psychiatrist_pmdc: "",
    psychiatrist_signature: "",
    psychiatrist_certified_at: "",

    trh_representative_name: "",
    trh_representative_designation: "",
    trh_representative_signature: "",
    trh_representative_date: new Date().toISOString().split("T")[0],

    witness1_name: "",
    witness1_signature: "",
    witness2_name: "",
    witness2_signature: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatient(patientId))
        .unwrap()
        .then((patient) => {
          if (patient) {
            let ageGender = "";
            if (patient.dob) {
              const birthYear = new Date(patient.dob).getFullYear();
              const currentYear = new Date().getFullYear();
              const age = currentYear - birthYear;
              ageGender = `${age} / ${patient.gender || ""}`;
            } else {
              ageGender = patient.gender || "";
            }

            setFormData((prev) => ({
              ...prev,
              patient_name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
              cnic_bform: patient.cnic || "",
              age_gender: ageGender,
              residential_address: patient.address || "",
            }));
          }
        })
        .catch((err) => console.error("Failed to fetch patient details:", err));
    }
  }, [dispatch, patientId]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.patient_name) newErrors.patient_name = "Patient Name is required";
    if (!formData.cnic_bform) newErrors.cnic_bform = "CNIC / B-Form is required";
    if (!formData.applicant_name) newErrors.applicant_name = "Applicant Name is required";
    if (!formData.applicant_relationship) newErrors.applicant_relationship = "Relationship is required";
    if (!formData.applicant_contact) newErrors.applicant_contact = "Contact is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill all required fields in the form.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      // Format psychiatrist certified date/time for DB if set
      let certTime = formData.psychiatrist_certified_at;
      if (certTime) {
        certTime = certTime.replace("T", " ");
        if (certTime.includes(".")) {
          certTime = certTime.split(".")[0];
        } else if (certTime.includes("+")) {
          certTime = certTime.split("+")[0];
        } else if (certTime.endsWith("Z")) {
          certTime = certTime.substring(0, certTime.length - 1);
        }
        certTime = certTime.trim();
        if (certTime.length === 16) {
          certTime = `${certTime}:00`;
        }
        if (certTime.length > 19) {
          certTime = certTime.substring(0, 19);
        }
      } else {
        certTime = null;
      }

      const payload = {
        ...formData,
        psychiatrist_certified_at: certTime,
      };

      const res = await dispatch(createEscortForm(payload)).unwrap();

      Swal.fire({
        title: "Success!",
        text: "Escort form added successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      navigate(`/home-visitation/escort-form/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
    } catch (err) {
      let errorMessage = "Failed to add Escort form";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (typeof err === "object" && err !== null) {
        errorMessage = Object.values(err).flat().join("\n");
      }
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        <div className="card">
          <div className="card-header border-bottom">
            <h5 className="mb-0">Add Involuntary Admission / Assisted Pick-Up Authorization Form</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <EscortForm
                formData={formData}
                setFormData={setFormData}
                errors={errors}
              />
              <div className="row px-4 pb-4">
                <div className="col-md-2 ms-auto">
                  <button type="submit" className="btn btn-primary w-100">
                    <i className="ri-save-3-line me-1"></i> Save
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddEscortForm;
